// Meeting tab — orchestrates list + CRUD + archive + Generate
// Occurrences for meeting tracking items. Uses the SummaryStrip +
// FilterChipBar + grouped MeetingTable trio. Deep-link /
// archive-banner / generate-occurrences / detail-modal behaviour is
// preserved.
//
// Filter state lives locally on the tab; FilterChipBar drives chip /
// search updates, which feed the pure applyMeetingChipFilters pass
// before grouping into parent/child/one-off rows. The includeArchived
// chip drives both the API request (so archived rows reach the
// client) and the client-side filter (so toggling off hides them
// without refetching).

import { useMemo, useState } from "react";
import Alert from "../../../common/Alert";
import TrackingTabShell from "../shared/TrackingTabShell";
import { useDeepLinkPageJump } from "../shared/useDeepLinkPageJump";
import { findMeetingDeepLinkTarget } from "./meetingDeepLink";
import ArchiveDialog from "../shared/ArchiveDialog";
import MeetingFormModal, {
  type MeetingFormMode,
} from "./MeetingFormModal";
import MeetingDetailExpand from "./MeetingDetailExpand";
import MeetingDetailModal from "./MeetingDetailModal";
import MarkHeldDialog, {
  type MarkHeldResult,
} from "./MarkHeldDialog";
import AddMinutesLinkDialog, {
  type AddMinutesLinkResult,
} from "./AddMinutesLinkDialog";
import MeetingSummaryStrip from "./MeetingSummaryStrip";
import MeetingFilterChipBar, {
  type MeetingFilterChipBarFilters,
} from "./MeetingFilterChipBar";
import MeetingActiveFilterSummary from "./MeetingActiveFilterSummary";
import MeetingTable, {
  type MeetingAction,
  type MeetingRow,
} from "./MeetingTable";
import { applyMeetingChipFilters } from "./applyMeetingChipFilters";
import { groupMeetings } from "./groupMeetings";
import {
  shouldAutoOpenOnCreate,
  shouldAutoOpenOnUpdate,
} from "../deliverable/serialiseRule";
import GenerateOccurrencesDialog from "../deliverable/GenerateOccurrencesDialog";
import TrackingPagination from "../shared/TrackingPagination";
import { useLookups } from "../../../../hooks/useLookups";
import { useManagedContract } from "../../../../hooks/useManagedContracts";
import {
  useArchiveTrackingItem,
  useCreateTrackingItem,
  useGenerateOccurrences,
  useTrackingItem,
  useTrackingItems,
  useUpdateTrackingItem,
} from "../../../../hooks/useTracking";
import type {
  CreateMeetingInput,
  Meeting,
  UpdateMeetingInput,
} from "../../../../types";

interface MeetingTabProps {
  contractId: string;
  itemIdFromUrl?: string | null;
}

const PAGE_SIZE = 25;

const EMPTY_FILTERS: MeetingFilterChipBarFilters = {
  cadence: [],
  status: [],
  minutes: [],
  dateRange: "all-time",
  includeArchived: false,
};

function formatDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU");
  } catch {
    return v;
  }
}

export default function MeetingTab({
  contractId,
  itemIdFromUrl,
}: MeetingTabProps) {
  const [filters, setFilters] =
    useState<MeetingFilterChipBarFilters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [filterResetCounter, setFilterResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [modalMode, setModalMode] = useState<MeetingFormMode | null>(null);
  const [modalSource, setModalSource] = useState<Meeting | undefined>();
  const [expandedScheduleIds, setExpandedScheduleIds] = useState<Set<string>>(
    new Set(),
  );
  const [preFilterExpandedSnapshot, setPreFilterExpandedSnapshot] = useState<
    Set<string> | null
  >(null);
  const [archiveTarget, setArchiveTarget] = useState<Meeting | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  const [expandedItem, setExpandedItem] = useState<Meeting | null>(null);
  const [archivedDetailItem, setArchivedDetailItem] = useState<Meeting | null>(
    null,
  );
  const [archiveBanner, setArchiveBanner] = useState(true);
  const [generateTarget, setGenerateTarget] = useState<Meeting | null>(null);
  const [markHeldTarget, setMarkHeldTarget] = useState<Meeting | null>(null);
  const [addMinutesTarget, setAddMinutesTarget] = useState<Meeting | null>(
    null,
  );

  const { data, isPending, error } = useTrackingItems(contractId, "meeting", {
    limit: PAGE_SIZE,
    includeArchived: filters.includeArchived,
  });

  const contractQuery = useManagedContract(contractId);
  const graceOverride = useMemo(
    () => ({
      graceDays: contractQuery.data?.meetingGraceDays ?? null,
      graceDaysByFrequency:
        contractQuery.data?.meetingGraceDaysByFrequency ?? null,
    }),
    [
      contractQuery.data?.meetingGraceDays,
      contractQuery.data?.meetingGraceDaysByFrequency,
    ],
  );

  const lookupQuery = useLookups("meeting-type");
  const meetingTypeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of lookupQuery.data?.items ?? []) {
      map[item.itemId] = item.label;
    }
    return map;
  }, [lookupQuery.data]);

  const items: Meeting[] = useMemo(
    () =>
      (data?.items ?? []).filter((i): i is Meeting => i.type === "meeting"),
    [data],
  );

  const filteredItems = useMemo(
    () => applyMeetingChipFilters(items, filters, search),
    [items, filters, search],
  );

  const rows: MeetingRow[] = useMemo(
    () => groupMeetings(filteredItems),
    [filteredItems],
  );

  // Client-side pagination over schedules (recurring parents count as
  // one schedule, one-offs count as one each). Children render nested
  // under their visible parent so we don't paginate them separately.
  const totalSchedules = rows.filter((r) => r.kind !== "child").length;
  const totalPages = Math.max(1, Math.ceil(totalSchedules / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedRows: MeetingRow[] = useMemo(() => {
    const topLevel = rows.filter((r) => r.kind !== "child");
    const start = (safePage - 1) * pageSize;
    const visibleTopIds = new Set(
      topLevel.slice(start, start + pageSize).map((r) => r.meeting.id),
    );
    return rows.filter((r) => {
      if (r.kind === "child") {
        return visibleTopIds.has(r.parent.id);
      }
      return visibleTopIds.has(r.meeting.id);
    });
  }, [rows, safePage, pageSize]);

  // Deep-link page-jump: when ?item=<id> targets a meeting on a later page,
  // jump the client-side paginator to its top-level schedule row (the
  // pagination unit). A child occurrence resolves to its parent's index and
  // that parent is auto-expanded so the child is visible; the target's own
  // view detail expand is opened too.
  const deepLinkTarget = useMemo(
    () => findMeetingDeepLinkTarget(itemIdFromUrl, rows),
    [itemIdFromUrl, rows],
  );
  useDeepLinkPageJump({
    itemId: itemIdFromUrl,
    targetIndex: deepLinkTarget.index,
    pageSize,
    onJump: (targetPage, _index, itemId) => {
      if (targetPage !== page) setPage(targetPage);
      const parentId = deepLinkTarget.expandParentId;
      if (parentId) {
        setExpandedScheduleIds((prev) => new Set(prev).add(parentId));
      }
      // Open the target row's view detail expand so the linked record is
      // readable without a second click.
      setHighlightRowId(itemId);
      setExpandedItem(deepLinkTarget.target);
    },
  });

  // After a PATCH lands the items list is refreshed; refresh the
  // expanded panel from that fresh list so status/heldDate changes
  // surface in the inline expand without the user re-clicking the row.
  const liveExpandedItem = useMemo(() => {
    if (!expandedItem) return null;
    return items.find((i) => i.id === expandedItem.id) ?? expandedItem;
  }, [expandedItem, items]);

  // `deepLinkInList` (not "in page") = the item exists anywhere in the loaded
  // list; when true the page-jump reveals it, so the banner stays suppressed.
  const deepLinkInList = itemIdFromUrl
    ? items.some((i) => i.id === itemIdFromUrl)
    : false;
  const missingItemQuery = useTrackingItem(
    contractId,
    "meeting",
    !deepLinkInList && itemIdFromUrl ? itemIdFromUrl : "",
  );
  const missingItem =
    missingItemQuery.data && missingItemQuery.data.type === "meeting"
      ? (missingItemQuery.data as Meeting)
      : null;

  const createMut = useCreateTrackingItem();
  const updateMut = useUpdateTrackingItem();
  const archiveMut = useArchiveTrackingItem();
  const generateMut = useGenerateOccurrences();

  const handleOpenCreate = () => {
    setModalSource(undefined);
    setModalMode("create");
  };
  const handleOpenEdit = (item: Meeting) => {
    setModalSource(item);
    setModalMode("edit");
  };
  const handleCloseModal = () => setModalMode(null);

  const handleCreate = async (input: CreateMeetingInput) => {
    const created = (await createMut.mutateAsync({
      contractId,
      type: "meeting",
      input,
    })) as Meeting | undefined;
    if (created?.id) setHighlightRowId(created.id);
    if (created && shouldAutoOpenOnCreate(created)) {
      setGenerateTarget(created);
    }
  };

  const handleUpdate = async (input: UpdateMeetingInput) => {
    if (!modalSource) return;
    await updateMut.mutateAsync({
      contractId,
      type: "meeting",
      itemId: modalSource.id,
      input,
    });
    if (shouldAutoOpenOnUpdate(modalSource.recurrenceRule, input)) {
      setGenerateTarget({
        ...modalSource,
        recurrenceRule: input.recurrenceRule,
      });
    }
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!archiveTarget) return;
    await archiveMut.mutateAsync({
      contractId,
      type: "meeting",
      itemId: archiveTarget.id,
      reason,
    });
    setArchiveTarget(null);
  };

  const handleGenerateConfirm = async (archiveOrphanIds?: string[]) => {
    if (!generateTarget) return;
    if (archiveOrphanIds && archiveOrphanIds.length > 0) {
      for (const orphanId of archiveOrphanIds) {
        await archiveMut.mutateAsync({
          contractId,
          type: "meeting",
          itemId: orphanId,
          reason: "Superseded by updated recurrence rule",
        });
      }
    }
    await generateMut.mutateAsync({
      contractId,
      type: "meeting",
      itemId: generateTarget.id,
    });
    setGenerateTarget(null);
  };

  const handleMarkHeldConfirm = async (result: MarkHeldResult) => {
    if (!markHeldTarget) return;
    await updateMut.mutateAsync({
      contractId,
      type: "meeting",
      itemId: markHeldTarget.id,
      input: {
        version: markHeldTarget.version,
        status: "held",
        heldDate: result.heldDate,
        evidenceLinks: result.evidenceLinks,
      },
    });
    setMarkHeldTarget(null);
  };

  const handleAddMinutesConfirm = async (result: AddMinutesLinkResult) => {
    if (!addMinutesTarget) return;
    await updateMut.mutateAsync({
      contractId,
      type: "meeting",
      itemId: addMinutesTarget.id,
      input: {
        version: addMinutesTarget.version,
        evidenceLinks: result.evidenceLinks,
      },
    });
    setAddMinutesTarget(null);
  };

  const handleToggleExpand = (parentId: string) => {
    setExpandedScheduleIds((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  const handleRowClick = (row: { kind: "child" | "one-off"; meeting: Meeting }) => {
    setExpandedItem((prev) => (prev?.id === row.meeting.id ? null : row.meeting));
  };

  const handleAction = (row: MeetingRow, action: MeetingAction) => {
    if (action === "edit") {
      handleOpenEdit(row.meeting);
      return;
    }
    if (action === "archive") {
      setArchiveTarget(row.meeting);
      return;
    }
    if (action === "primary" && row.kind === "parent") {
      setGenerateTarget(row.meeting);
    }
  };

  const hasActiveChipFilter = (
    f: MeetingFilterChipBarFilters,
    s: string,
  ): boolean =>
    f.cadence.length > 0 ||
    f.status.length > 0 ||
    f.minutes.length > 0 ||
    f.dateRange !== "all-time" ||
    f.includeArchived ||
    s.trim().length > 0;

  const computeMatchingParents = (
    nextFilters: MeetingFilterChipBarFilters,
    nextSearch: string,
  ): Set<string> => {
    const matched = applyMeetingChipFilters(items, nextFilters, nextSearch);
    const parentIds = new Set<string>();
    for (const m of matched) {
      if (m.parentId) parentIds.add(m.parentId);
    }
    return parentIds;
  };

  const handleFiltersChange = (next: MeetingFilterChipBarFilters) => {
    const wasActive = hasActiveChipFilter(filters, search);
    const isActive = hasActiveChipFilter(next, search);

    if (!wasActive && isActive) {
      setPreFilterExpandedSnapshot(expandedScheduleIds);
      setExpandedScheduleIds(computeMatchingParents(next, search));
    } else if (wasActive && !isActive) {
      if (preFilterExpandedSnapshot) {
        setExpandedScheduleIds(preFilterExpandedSnapshot);
        setPreFilterExpandedSnapshot(null);
      }
    } else if (wasActive && isActive) {
      setExpandedScheduleIds(computeMatchingParents(next, search));
    }

    setFilters(next);
  };

  const handleSearchChange = (next: string) => {
    const wasActive = hasActiveChipFilter(filters, search);
    const isActive = hasActiveChipFilter(filters, next);
    if (!wasActive && isActive) {
      setPreFilterExpandedSnapshot(expandedScheduleIds);
      setExpandedScheduleIds(computeMatchingParents(filters, next));
    } else if (wasActive && !isActive) {
      if (preFilterExpandedSnapshot) {
        setExpandedScheduleIds(preFilterExpandedSnapshot);
        setPreFilterExpandedSnapshot(null);
      }
    } else if (wasActive && isActive) {
      setExpandedScheduleIds(computeMatchingParents(filters, next));
    }
    setSearch(next);
  };

  const handleClearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setFilterResetCounter((c) => c + 1);
    if (preFilterExpandedSnapshot) {
      setExpandedScheduleIds(preFilterExpandedSnapshot);
      setPreFilterExpandedSnapshot(null);
    } else {
      setExpandedScheduleIds(new Set());
    }
  };

  return (
    <div className="space-y-4">
      {itemIdFromUrl && !deepLinkInList && missingItem && archiveBanner && (
        <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {missingItem.archivedAt
                ? `This meeting was archived on ${formatDate(
                    missingItem.archivedAt.slice(0, 10),
                  )}.`
                : `This meeting is not shown in the current list.`}
            </span>
            <button
              type="button"
              className="underline"
              onClick={() => setArchivedDetailItem(missingItem)}
            >
              View archived item
            </button>
          </div>
        </Alert>
      )}

      {itemIdFromUrl &&
        !deepLinkInList &&
        missingItemQuery.error &&
        archiveBanner && (
          <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
            Item not found. It may have been deleted.
          </Alert>
        )}

      <MeetingSummaryStrip meetings={items} graceOverride={graceOverride} />

      <MeetingActiveFilterSummary
        filters={filters}
        search={search}
        onClearAll={handleClearAllFilters}
      />

      <MeetingFilterChipBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        search={search}
        onSearchChange={handleSearchChange}
        resetCounter={filterResetCounter}
      />

      <TrackingTabShell
        type="meeting"
        onAdd={handleOpenCreate}
        includeArchived={filters.includeArchived}
        onToggleArchived={(v) => {
          handleFiltersChange({ ...filters, includeArchived: v });
        }}
        isEmpty={!isPending && !error && items.length === 0}
        showIncludeArchived={false}
      >
        {isPending ? (
          <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-body text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
            Loading meetings…
          </div>
        ) : error ? (
          <Alert variant="error">Failed to load meetings.</Alert>
        ) : (
          <>
            <MeetingTable
              rows={pagedRows}
              expandedScheduleIds={expandedScheduleIds}
              onToggleExpand={handleToggleExpand}
              onRowClick={handleRowClick}
              onAction={handleAction}
              highlightRowId={highlightRowId}
              graceOverride={graceOverride}
              expandedItemId={liveExpandedItem?.id ?? null}
              renderExpandedRow={(meeting) => (
                <MeetingDetailExpand
                  key={meeting.id}
                  item={meeting}
                  meetingTypeLabel={meetingTypeLabels[meeting.meetingType]}
                  onEdit={
                    meeting.archivedAt
                      ? undefined
                      : () => {
                          handleOpenEdit(meeting);
                          setExpandedItem(null);
                        }
                  }
                  onMarkHeld={
                    meeting.archivedAt
                      ? undefined
                      : () => setMarkHeldTarget(meeting)
                  }
                  onAddMinutes={
                    meeting.archivedAt
                      ? undefined
                      : () => setAddMinutesTarget(meeting)
                  }
                />
              )}
            />
            <TrackingPagination
              page={safePage}
              pageSize={pageSize}
              total={totalSchedules}
              noun="schedule"
              // Count actual meeting events (one-offs + child occurrences),
              // not the recurring-schedule parent metadata records. The
              // parent is a schedule, not an event — counting it would
              // produce an off-by-one against the tab badge.
              secondaryCount={{ value: items.filter((i) => !(i.recurrenceRule && !i.parentId)).length, noun: "meeting" }}
              testIdPrefix="meeting"
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </TrackingTabShell>

      <MeetingFormModal
        isOpen={modalMode === "create"}
        onClose={handleCloseModal}
        mode="create"
        contractId={contractId}
        onCreate={handleCreate}
        isSubmitting={createMut.isPending}
        contractEndDate={contractQuery.data?.endDate}
      />
      <MeetingFormModal
        isOpen={modalMode === "edit"}
        onClose={handleCloseModal}
        mode="edit"
        contractId={contractId}
        source={modalSource}
        onUpdate={handleUpdate}
        isSubmitting={updateMut.isPending}
        contractEndDate={contractQuery.data?.endDate}
      />

      <ArchiveDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        itemLabel={
          archiveTarget
            ? `meeting "${archiveTarget.name}"`
            : "this meeting"
        }
        isSubmitting={archiveMut.isPending}
      />

      <GenerateOccurrencesDialog
        isOpen={!!generateTarget}
        onClose={() => setGenerateTarget(null)}
        contractId={contractId}
        type="meeting"
        item={generateTarget}
        onConfirm={handleGenerateConfirm}
        isSubmitting={generateMut.isPending}
      />

      <MeetingDetailModal
        isOpen={!!archivedDetailItem}
        onClose={() => setArchivedDetailItem(null)}
        item={archivedDetailItem}
      />

      <MarkHeldDialog
        isOpen={!!markHeldTarget}
        onClose={() => setMarkHeldTarget(null)}
        onConfirm={handleMarkHeldConfirm}
        meetingName={markHeldTarget?.name ?? "this meeting"}
        currentEvidenceLinks={markHeldTarget?.evidenceLinks ?? []}
      />

      <AddMinutesLinkDialog
        isOpen={!!addMinutesTarget}
        onClose={() => setAddMinutesTarget(null)}
        onConfirm={handleAddMinutesConfirm}
        meetingName={addMinutesTarget?.name ?? "this meeting"}
        currentEvidenceLinks={addMinutesTarget?.evidenceLinks ?? []}
      />
    </div>
  );
}
